import { createContext, useContext, useState, useCallback } from "react";

const AuthContext = createContext(null);

const DEFAULT_PERMISSIONS = {
  staff_dashboard: true,
  quota_history: true,
  reports: true,
  blacklist: true,
  staff_dropdown: false,
  admin_panel: false,
  form_editor: false,
};

export const AuthProvider = ({ children }) => {
  const [gateUnlocked, setGateUnlocked] = useState(() => sessionStorage.getItem("gateUnlocked") === "true");
  const [isAuthenticated, setIsAuthenticated] = useState(() => sessionStorage.getItem("authenticated") === "true");
  const [currentUser, setCurrentUser] = useState(() => sessionStorage.getItem("currentUser") || "");
  const [isAdmin, setIsAdmin] = useState(() => sessionStorage.getItem("isAdmin") === "true");
  const [userRole, setUserRole] = useState(() => sessionStorage.getItem("userRole") || "user");
  const [canViewReports, setCanViewReports] = useState(() => sessionStorage.getItem("canViewReports") === "true");
  const [permissions, setPermissions] = useState(() => {
    const stored = sessionStorage.getItem("permissions");
    return stored ? JSON.parse(stored) : DEFAULT_PERMISSIONS;
  });
  const [discordId, setDiscordId] = useState(() => sessionStorage.getItem("discordId") || "");

  const unlockGate = useCallback(() => {
    sessionStorage.setItem("gateUnlocked", "true");
    setGateUnlocked(true);
  }, []);

  const login = useCallback((displayName, adminStatus, role, viewReports, perms, dId) => {
    setIsAuthenticated(true);
    setCurrentUser(displayName);
    setIsAdmin(adminStatus);
    setUserRole(role || "user");
    setCanViewReports(viewReports || false);
    setDiscordId(dId || "");
    const userPerms = perms || DEFAULT_PERMISSIONS;
    setPermissions(userPerms);
    sessionStorage.setItem("isAdmin", adminStatus ? "true" : "false");
    sessionStorage.setItem("userRole", role || "user");
    sessionStorage.setItem("canViewReports", viewReports ? "true" : "false");
    sessionStorage.setItem("permissions", JSON.stringify(userPerms));
    sessionStorage.setItem("discordId", dId || "");
  }, []);

  const logout = useCallback(() => {
    sessionStorage.clear();
    setIsAuthenticated(false);
    setCurrentUser("");
    setIsAdmin(false);
    setUserRole("user");
    setCanViewReports(false);
    setDiscordId("");
    setGateUnlocked(false);
    setPermissions(DEFAULT_PERMISSIONS);
  }, []);

  const value = {
    gateUnlocked,
    isAuthenticated,
    currentUser,
    isAdmin,
    userRole,
    canViewReports,
    permissions,
    discordId,
    unlockGate,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
